import type { FilterOptions, FilterValues } from "../ui/optimized-filters"
import type { TGroupResponseResult } from "@/models/result.model"
import type { TSubUnit, Unit, UnitStatus } from "@/models/units.model"
import type { TCompetitionData } from "@/services/settings/competition"
import type { TStartList } from "@/services/startLists/start-list-service"
import type { AllISO3Codes } from "@/utils/lang/iso-map"
import { UTCDate } from "@date-fns/utc"
import { /* useMutation, */ useQuery } from "@tanstack/react-query"
import { format } from "date-fns"
import { ChevronDown, ChevronUp, Clock, Filter, InfoIcon, MapPin, Settings } from "lucide-react"
import { useLocale, useTranslations } from "next-intl"
import Image from "next/image"
import { useParams, useRouter } from "next/navigation"
import { useQueryState } from "nuqs"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { Icon } from "@/components/ui/icon"
import { useChannelParamsSync } from "@/hooks/use-channel-params-sync"
import { useChannelSyncContext } from "@/hooks/use-channel-sync-context"
import { useConfirm } from "@/hooks/use-confirm"
import { Link } from "@/i18n/navigation"
import { env } from "@/lib/env"
import { cn } from "@/lib/utils"
import { languageParser } from "@/mappers/language.mapper"
import { Rsc } from "@/mappers/rsc.mapper"
import { EventQueryOptions, StartListQueryOptions, UnitQueryOptions } from "@/queries/disciplines.factory"
import { competitionService } from "@/services/settings/competition"
import { phasesService } from "@/services/settings/phases-service"
import { unitsService } from "@/services/settings/units"

import { useParamHintsStore } from "@/stores/param-hints-store"

import { getCountryFlagIcon } from "@/utils/lang/iso-convert"
import { ConfigurableUnitDialog } from "../settings/sport-events/configurable-unit-dialog"
import { AlertWrapper } from "../ui/alert"
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import { Card, CardContent } from "../ui/card"
import { ExtendedCombobox } from "../ui/extended-combobox"
import { Input } from "../ui/input"
import { OptimizedFilters } from "../ui/optimized-filters"
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarHeader, useSidebar } from "../ui/sidebar"
import { TooltipWrapper } from "../ui/tooltip"
// import { authService } from "@/services/auth.service"
// Función para obtener colores del badge del status
function getUnitStatusBadgeColor(status: UnitStatus | undefined) {
  switch (status) {
    case "START_LIST":
      return "bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-500"
    case "GETTING_READY":
      return "bg-orange-500 hover:bg-orange-600 text-white border-orange-500"
    case "RUNNING":
      return "bg-green-500 hover:bg-green-600 text-white border-green-500"
    case "PROGRAMED_PAUSE":
      return "bg-purple-500 hover:bg-purple-600 text-white border-purple-500"
    case "FINISHED":
      return "bg-indigo-500 hover:bg-indigo-600 text-white border-indigo-500"
    case "UNOFFICIAL":
      return "bg-blue-500 hover:bg-blue-600 text-white border-blue-500"
    case "OFFICIAL":
      return "bg-red-500 hover:bg-red-600 text-white border-red-500"
    case "PROTESTED":
      return "bg-gray-500 hover:bg-gray-600 text-white border-gray-500"
    case "UNSCHEDULED":
    case "SCHEDULED":
      return "bg-gray-100 hover:bg-gray-200 text-gray-800 border-gray-300"
    case undefined:
    default:
      return "bg-gray-100 hover:bg-gray-200 text-gray-800 border-gray-300"
  }
}

// Función para obtener el color del borde izquierdo del card
function getUnitBorderColor(status: UnitStatus | undefined) {
  switch (status) {
    case "START_LIST":
      return "border-l-yellow-500 border-l-4"
    case "GETTING_READY":
      return "border-l-orange-500 border-l-4"
    case "RUNNING":
      return "border-l-green-500 border-l-4"
    case "PROGRAMED_PAUSE":
      return "border-l-purple-500 border-l-4"
    case "FINISHED":
      return "border-l-indigo-500 border-l-4"
    case "UNOFFICIAL":
      return "border-l-blue-500 border-l-4"
    case "OFFICIAL":
      return "border-l-red-500 border-l-4"
    case "PROTESTED":
      return "border-l-gray-500 border-l-4"
    case "UNSCHEDULED":
    case "SCHEDULED":
      return "border-l-gray-200 border-l-4"
    case undefined:
    default:
      return "border-l-gray-200 border-l-4"
  }
}

// Helper function para detectar equipos mixtos y obtener información correcta
function getTeamInfo(participantId: string, globalParticipant: any, isTeamLike: boolean, disciplineCode: string, tCommon: any) {
  // Si no es un equipo (participante individual), usar bandera normal
  if (!isTeamLike || !globalParticipant) {
    return {
      name: "",
      flagSrc: "/placeholder.svg",
      delegation: "",
      isMixed: false,
      isSportPicto: false,
      useNormalFlag: true,
      isDuo: false,
    }
  }

  const individualParticipants = (globalParticipant as TGroupResponseResult).participants || []

  // Si no hay participantes individuales, usar bandera normal
  if (individualParticipants.length === 0) {
    return {
      name: "",
      flagSrc: "/placeholder.svg",
      delegation: "",
      isMixed: false,
      isSportPicto: false,
      useNormalFlag: true,
      isDuo: false,
    }
  }

  // Verificar si es dupla/equipo de 2
  const isDuo = individualParticipants.length === 2

  // Verificar si hay delegaciones diferentes
  const delegations = [...new Set(individualParticipants.map((p: { delegation: string }) => p.delegation).filter(Boolean))]
  const isMixed = delegations.length > 1

  // Para duplas o equipos con delegación CO, mostrar el picto completo
  if (delegations.includes("CO") && !isMixed) {
    return {
      name: tCommon("mixed"),
      flagSrc: `/pictos/${disciplineCode}.png`,
      delegation: "CO",
      isMixed: false,
      isSportPicto: true,
      useNormalFlag: false,
      isDuo,
    }
  }

  if (isMixed) {
    return {
      name: tCommon("mixed"),
      flagSrc: `/pictos/${disciplineCode}.png`,
      delegation: "CO", // Ta  jharcodeado por falta de tiempo xd
      isMixed: true,
      isSportPicto: true,
      useNormalFlag: false,
      isDuo,
    }
  }

  // Si no es mixto, usar la información del primer participante
  const firstParticipant = individualParticipants[0]
  if (!firstParticipant) {
    return {
      name: "",
      flagSrc: "/placeholder.svg",
      delegation: "",
      isMixed: false,
      isSportPicto: false,
      useNormalFlag: true,
      isDuo: false,
    }
  }

  const icon = getCountryFlagIcon(firstParticipant.delegation as AllISO3Codes) || "/placeholder.svg"

  return {
    name: "",
    flagSrc: icon,
    delegation: firstParticipant.delegation || "",
    isMixed: false,
    isSportPicto: false,
    useNormalFlag: false,
    isDuo: false,
  }
}

type UnitItemProps = {
  unit: Unit
  eventCode?: string
  selectedUnit?: string
  type: "start-list" | "data-entry" | "integrations" | "main" | "simple-data-entry"
  eventDescription: string
  selectedDate?: string
  selectedTatami?: string
  selectedStatus?: string
  search?: string
  competitions?: TCompetitionData[]
  onParticipantSelect: (participantId: string) => void
  startListParentData?: TStartList | undefined
}

function UnitItem({
  unit,
  eventCode,
  selectedUnit,
  type,
  eventDescription,
  selectedDate,
  selectedTatami,
  selectedStatus,
  search,
  competitions,
  onParticipantSelect,
  startListParentData,
}: UnitItemProps): React.JSX.Element {
  const itemRef = useRef<HTMLAnchorElement>(null)
  const { state } = useSidebar()
  const { triggerSync } = useChannelSyncContext()
  const router = useRouter()
  const confirm = useConfirm()
  const tFloating = useTranslations("floating_units")
  const t = useTranslations()
  const tCommon = useTranslations("common")
  const language = useLocale()
  const { flashingElements, activeParams } = useParamHintsStore()

  // Estado para el modal de configuración
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false)
  const [isSavingConfig, setIsSavingConfig] = useState(false)

  // Handlers para el modal de configuración
  const handleOpenConfig = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsConfigModalOpen(true)
  }

  const handleCloseConfig = () => {
    setIsConfigModalOpen(false)
  }

  const handleSaveConfig = async (unitData: any) => {
    setIsSavingConfig(true)
    try {
      console.log("Guardando configuración de unidad:", unitData)

      // Llamar al servicio de actualización
      await unitsService.updateUnit({ ...unitData, code: unit.code })

      toast.success("Configuración guardada exitosamente")
      setIsConfigModalOpen(false)

      // Opcional: recargar los datos
      // window.location.reload()
    }
    catch (error) {
      console.error("Error saving unit config:", error)
      toast.error("Error al guardar la configuración")
    }
    finally {
      setIsSavingConfig(false)
    }
  }

  // Obtener start list para mostrar participantes
  const { data: startList } = useQuery(StartListQueryOptions.byUnitCode(unit.code))

  // const startList = startListParentData
  // Obtener configuración de la fase para el modal (misma query que usa el modal original)
  const { data: phaseSettings } = useQuery(UnitQueryOptions.getSettingsByPhaseCode(unit.metadata?.phaseCode || ""))

  // Obtener información de la fase usando el phaseCode
  const { data: unitPhaseInfo } = useQuery({
    queryKey: ["phase", unit.metadata?.phaseCode],
    queryFn: async () => phasesService.getPhaseByCode(unit.metadata?.phaseCode || ""),
    enabled: !!unit.metadata?.phaseCode,
    staleTime: 5 * 60 * 1000, // 5 minutos
  })

  // dont scroll into view if the sidebar is collapsed
  if (selectedUnit === unit.code && itemRef.current && state === "expanded") {
    itemRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const paramsFilters = useMemo(() => {
    const availableSearchParams = []

    if (eventCode) {
      availableSearchParams.push(`event=${eventCode}`)
    }

    if (selectedDate) {
      availableSearchParams.push(`unit-date=${selectedDate}`)
    }

    if (selectedTatami) {
      availableSearchParams.push(`tatami=${selectedTatami}`)
    }

    if (selectedStatus) {
      availableSearchParams.push(`status=${selectedStatus}`)
    }

    if (search) {
      availableSearchParams.push(`unit-search=${encodeURIComponent(search)}`)
    }

    return availableSearchParams.join("&")
  }, [eventCode, selectedDate, selectedTatami, selectedStatus, search])

  // Formatear fecha y hora en zona horaria de Brasilia (UTC-3)
  const formatDateTime = (dateString: string) => {
    // Crear fecha en UTC y convertir a zona horaria de Brasilia
    const utcDate = new Date(dateString)

    // Convertir a zona horaria de Brasilia (UTC-3)
    const brasiliaDate = new Date(utcDate.getTime() + (3 * 60 * 60 * 1000))

    const dateStr = brasiliaDate.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })

    const timeStr = brasiliaDate.toLocaleTimeString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })

    return { dateStr, timeStr }
  }

  const { dateStr, timeStr } = unit.dateInfo?.startDate
    ? formatDateTime(unit.dateInfo.startDate)
    : { dateStr: "", timeStr: "" }

  // Obtener información de participantes/enfrentamiento
  const getMatchInfo = () => {
    // Buscar la competición que coincida con el sportEvent y género de la unit
    const matchingCompetition = competitions?.find(comp =>
      comp.metadata?.sportEvent === unit.metadata?.sportEvent
      && comp.metadata?.gender === unit.metadata?.gender,
    )

    if (matchingCompetition) {
      // Usar directamente el nombre completo que ya viene en el campo name
      // Solo agregar la categoría si no es "GENERAL" o si es diferente al nombre de la competencia
      const category = unit.metadata?.category
      const competitionName = matchingCompetition.name || ""
      
      // Si la categoría es "GENERAL" o está vacía, solo mostrar el nombre de la competencia
      if (!category || category === "GENERAL") {
        return competitionName.trim()
      }
      
      // Si la categoría no está incluida en el nombre de la competencia, agregarla
      if (!competitionName.toLowerCase().includes(category.toLowerCase())) {
        return `${category} - ${competitionName}`.trim()
      }
      
      return competitionName.trim()
    }

    // Fallback: usar description.long + description.short de la unit
    if (unit.description?.long || unit.description?.short) {
      const long = unit.description.long || ""
      const short = unit.description.short || ""
      const category = unit.metadata?.category
      
      // Solo agregar la categoría si no es "GENERAL"
      if (!category || category === "GENERAL") {
        return `${long} ${short}`.trim()
      }
      
      return `${category} - ${long} ${short}`.trim()
    }

    // Fallback final: construir el título manualmente si no hay nombre
    const { gender, sportEvent, phase } = unit.metadata || {}
    let matchTitle = ""

    if (gender === "M")
      matchTitle += "Men's "
    else if (gender === "W")
      matchTitle += "Women's "
    else if (gender === "X")
      matchTitle += "Mixed "

    if (sportEvent)
      matchTitle += `${sportEvent} `
    if (phase)
      matchTitle += phase.replace(/-/g, " ").trim()

    return matchTitle.trim() || eventDescription
  }

  // Obtener información del enfrentamiento
  const getMatchupInfo = () => {
    const groups = startList?.groups || []
    const participants = startList?.participants || []

    const startListParticipants = participants.length > 0 ? participants : groups

    if (startListParticipants.length === 0) {
      return { participant1: "TBD", participant2: "TBD", flag1: "", flag2: "", teamInfo1: null, teamInfo2: null }
    }
    const byeParticipant = {
      name: "BYE",
      surname: "",
      delegation: "",
      participantId: "bye",
    }

    const homeParticipant = startListParticipants.find(participant => participant.startingSortOrder === 0) ?? {
      ...byeParticipant,
      startingSortOrder: 0,
    }
    const awayParticipant = startListParticipants.find(participant => participant.startingSortOrder === 1) ?? {
      ...byeParticipant,
      startingSortOrder: 1,
    }

    // Determinar si es un equipo
    const isTeamLike = unit.unitTypeCode === "TEAM" || unit.unitTypeCode === "HTEAM" || unit.unitTypeCode === "COUP" || unit.unitTypeCode === "HCOUP"

    // Obtener información de equipos mixtos
    const teamInfo1 = getTeamInfo(
      homeParticipant && "participantId" in homeParticipant ? homeParticipant.participantId : homeParticipant?.groupId || "",
      homeParticipant,
      isTeamLike,
      unit.metadata?.discipline?.toLowerCase() || "",
      tCommon,
    )

    const teamInfo2 = getTeamInfo(
      awayParticipant && "participantId" in awayParticipant ? awayParticipant.participantId : awayParticipant?.groupId || "",
      awayParticipant,
      isTeamLike,
      unit.metadata?.discipline?.toLowerCase() || "",
      tCommon,
    )

    return {
      participant1: homeParticipant?.name ? `${homeParticipant.surname ?? ""} ${homeParticipant.name ?? ""}` : "TBD",
      participant2: awayParticipant?.name ? `${awayParticipant.surname ?? ""} ${awayParticipant.name ?? ""}` : "TBD",
      flag1: homeParticipant?.delegation || "",
      flag2: awayParticipant?.delegation || "",
      id1: homeParticipant && "participantId" in homeParticipant ? homeParticipant?.participantId : homeParticipant?.groupId || "",
      id2: awayParticipant && "participantId" in awayParticipant ? awayParticipant?.participantId : awayParticipant?.groupId || "",
      teamInfo1,
      teamInfo2,
    }
  }

  const matchup = getMatchupInfo()

  // Verificar si se debe mostrar el enfrentamiento
  const shouldShowMatchup = () => {
    const hiddenTypes = ["ATH", "COUP", "TEAM"]
    return !hiddenTypes.includes(unit.unitTypeCode || "")
  }

  const [selectedParticipantColor, setSelectedParticipantColor] = useState<string | null>(null) // hook para cambiar el color del div del participante seleccionado

  // Get flash color for participant parameters
  const getParticipantFlashColor = () => {
    if (!flashingElements.has("participant"))
      return ""
    const participantParam = activeParams.find(p => p.type === "participant")
    return participantParam?.color || "#10b981"
  }

  const isParticipantFlashing = flashingElements.has("participant")

  // Championship-based overrides: usar env para determinar JJB
  const isJJBEnv = env.NEXT_PUBLIC_CHAMPIONSHIP === "JJB"
  // Si NO es JJB, usar solo bandera por delegación; si es JJB, usar lógica original (teamInfo)
  const useOnlyFlag = !isJJBEnv

  // Función para detectar si se debe mostrar confirmación
  const shouldShowConfirmation = () => {
    // Verificar si estamos en la misma unidad (no hay cambio)
    if (selectedUnit === unit.code) {
      return false
    }

    // Solo mostrar confirmación si la unidad actual está en estado RUNNING
    // Para obtener el status de la unidad actual, necesitamos acceder a su información
    const currentUnitStatus = sessionStorage.getItem(`match_status_${selectedUnit}`)
      || sessionStorage.getItem(`timer_status_${selectedUnit}`)

    // Mostrar confirmación solo cuando la unidad actual esté en RUNNING
    return currentUnitStatus === "RUNNING"
  }

  // Función para manejar el click con confirmación
  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    // Si el url contiene "integrations" o "simple-data-entry", forzar la ruta
    const isForcedType
      = type === "integrations"
        || type === "simple-data-entry"
        || window.location.pathname.includes("/integrations")
        || window.location.pathname.includes("/simple-data-entry")
    const forcedType = window.location.pathname.includes("/integrations") || type === "integrations"
      ? "integrations"
      : (window.location.pathname.includes("/simple-data-entry") || type === "simple-data-entry"
          ? "simple-data-entry"
          : type)
    const targetUrl = `/disciplines/${unit.metadata.discipline.toLowerCase()}/${isForcedType ? forcedType : type}/${unit.code}${paramsFilters ? `?${paramsFilters}` : ""}`

    if (shouldShowConfirmation()) {
      const confirmed = await confirm({
        title: tFloating("confirm_unit_change"),
        description: tFloating("confirm_unit_change_description"),
        confirmText: tFloating("continue_anyway"),
        cancelText: tFloating("stay_here"),
      })

      if (confirmed) {
        // Limpiar datos no guardados si el usuario confirma
        if (selectedUnit) {
          sessionStorage.removeItem(`unsaved_data_${selectedUnit}`)
          sessionStorage.removeItem(`boxing_scores_${selectedUnit}`)
          sessionStorage.removeItem(`timer_status_${selectedUnit}`)
          sessionStorage.removeItem(`match_status_${selectedUnit}`)
        }
        triggerSync(unit.code, "sidebar")
        router.push(targetUrl)
      }
      // Si no confirma, no hacer nada (permanecer en la unidad actual)
    }
    else {
      triggerSync(unit.code, "sidebar")
      // Es la misma unidad, navegar directamente sin confirmación
      router.push(targetUrl)
    }
  }

  const handleParticipantClick = async (e: React.MouseEvent, participantId?: string) => {
    e.preventDefault()

    // Verificar si es la misma unidad y si hay un participantId, hacemos la seleccion de participante
    if (selectedUnit === unit.code) {
      if (!participantId)
        return

      onParticipantSelect(participantId)

      setSelectedParticipantColor(participantId)

      if (participantId === matchup.id1) {
        toast.success(matchup.participant1)
      }

      if (participantId === matchup.id2) {
        toast.success(matchup.participant2)
      }
    }
    else {
      // Si no es la misma unidad osea un cambio llmams a handleClick
      await handleClick(e as React.MouseEvent<HTMLAnchorElement>)
    }
  }

  return (
    <Link
      ref={itemRef}
      className="group block"
      href={`/disciplines/${unit.metadata.discipline.toLowerCase()}/${type}/${unit.code}${paramsFilters ? `?${paramsFilters}` : ""}`}
      onClick={(e) => { void handleParticipantClick(e) }}
    >
      <Card
        className={cn(
          "transition-all duration-200 cursor-pointer py-0 min-h-0 min-w-0 bg-card",
          getUnitBorderColor(unit.status),
          selectedUnit === unit.code && "ring-2 ring-primary border-primary",
          "relative",
        )}
      >
        <CardContent className="flex min-h-0 min-w-0 flex-col gap-1 p-2 md:p-2.5">
          {/* Botones en esquina inferior derecha */}
          <div className="absolute bottom-1 right-1 z-10 flex gap-1">
            {/* Botón de subunit si existe */}
            {unit.subUnits && unit.subUnits.length > 0 && (
              <TooltipWrapper content={`Ver ${unit.subUnits.length} Subunidades: ${unit.subUnits.map((s: TSubUnit) => s.name).join(", ")}`}>
                <Button
                  className="h-6 w-6 p-0 hover:bg-blue-500/10"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    console.log("Subunits:", unit.subUnits)
                    toast.success(`${unit.subUnits?.length} Subunidades: ${unit.subUnits?.map((s: TSubUnit) => s.name).join(", ")}`)
                  }}
                  variant="ghost"
                >
                  <span className="text-xs font-bold text-blue-600">{unit.subUnits.length}</span>
                </Button>
              </TooltipWrapper>
            )}

            {/* Botón de configuración */}
            <TooltipWrapper content={tFloating("configure_unit")}>
              <Button
                className="h-6 w-6 p-0 hover:bg-primary/10"
                onClick={handleOpenConfig}
                variant="ghost"
              >
                <Settings className="h-3.5 w-3.5" />
              </Button>
            </TooltipWrapper>
          </div>

          {/* Header con área/pista */}
          <div className="mb-0.5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-0.5 text-xs text-foreground">
              <MapPin className="h-2.5 w-2.5" />
              {typeof unit.location === "object" && unit.location?.description?.long
                ? (
                    <TooltipWrapper content={unit.location.description.long}>
                      <span className="max-w-[60px] truncate">{unit.location.description.long}</span>
                    </TooltipWrapper>
                  )
                : (
                    <span className="max-w-[60px] truncate text-foreground">{t("N/A")}</span>
                  )}
            </div>
            <div className="flex items-center gap-1">
              <Badge
                className={cn(
                  "text-[10px]",
                  getUnitStatusBadgeColor(unit.status),
                )}
                variant="secondary"
              >
                {unit.status}
              </Badge>
            </div>
          </div>

          {/* Nombre de la unidad - Centro */}
          <div className="mb-0.5 text-center">
            <TooltipWrapper content={getMatchInfo()}>
              <h3 className="truncate text-sm leading-tight font-semibold text-foreground">
                {getMatchInfo()}
              </h3>
            </TooltipWrapper>
            {/* Descripción corta de la unit */}
            {unit.description?.short && (
              <p className="mt-0.5 truncate text-xs text-foreground">
                {unit.description.short}
              </p>
            )}
            {/* Descripción de la fase */}
            {unitPhaseInfo?.description?.long && (
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {unitPhaseInfo.description.long}
              </p>
            )}
          </div>

          {/* Enfrentamiento - Compact row */}
          {shouldShowMatchup() && (
            <div className="mb-0.5 flex items-center justify-center gap-1 text-xs text-foreground">
              {/* <div onClick={(e) => { void getToken() }}>
                generar token
              </div> */}
              <div
                className={cn(
                  `flex flex-col items-center gap-0.5 flex-[3] cursor-pointer transition-all`,
                  selectedUnit === unit.code && "hover:bg-primary/10 hover:text-primary",
                  selectedParticipantColor === matchup.id1 && selectedUnit === unit.code ? "bg-primary text-white" : "",
                  isParticipantFlashing && selectedUnit === unit.code && "animate-pulse",
                )}
                onClick={(e) => { void handleParticipantClick(e, matchup.id1) }}
                style={isParticipantFlashing && selectedUnit === unit.code && selectedParticipantColor !== matchup.id1
                  ? {
                      backgroundColor: getParticipantFlashColor(),
                      borderRadius: "4px",
                      color: "white",
                    }
                  : undefined}
              >
                <span className="max-w-[100px] truncate">
                  {matchup.teamInfo1?.isMixed ? matchup.teamInfo1.name : matchup.participant1}
                </span>
                <div className="flex items-center gap-0.5">
                  {(matchup.flag1 || matchup.teamInfo1) && (
                    <Image
                      alt={useOnlyFlag
                        ? matchup.flag1
                        : (matchup.teamInfo1?.useNormalFlag ? matchup.flag1 : (matchup.teamInfo1?.delegation || matchup.flag1))}
                      className={cn("rounded-sm", matchup.teamInfo1?.isSportPicto
                        ? (matchup.teamInfo1?.isDuo ? "h-5 w-8 object-contain" : "h-4 w-6 object-cover")
                        : "h-3.5 w-4.5 object-cover")}
                      height={matchup.teamInfo1?.isSportPicto ? (matchup.teamInfo1?.isDuo ? 20 : 16) : 10}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.src = "/flags/small/un.webp"
                      }}
                      src={useOnlyFlag
                        ? (getCountryFlagIcon(matchup.flag1 as AllISO3Codes) || "/placeholder.svg")
                        : (matchup.teamInfo1?.useNormalFlag
                            ? (getCountryFlagIcon(matchup.flag1 as AllISO3Codes) || "/placeholder.svg")
                            : (matchup.teamInfo1?.flagSrc || "/placeholder.svg"))}
                      width={matchup.teamInfo1?.isSportPicto ? (matchup.teamInfo1?.isDuo ? 32 : 24) : 14}
                    />
                  )}
                  <span className="max-w-[40px] truncate">
                    {useOnlyFlag
                      ? matchup.flag1
                      : (matchup.teamInfo1?.useNormalFlag ? matchup.flag1 : (matchup.teamInfo1?.delegation || matchup.flag1))}
                  </span>
                </div>
              </div>
              <span className="mx-0.5 flex flex-[1] justify-center text-foreground">
                {"\u00B7"}
              </span>
              <div
                className={cn(
                  `flex flex-col items-center gap-0.5 flex-[3] cursor-pointer transition-all`,
                  selectedUnit === unit.code && "hover:bg-primary/10 hover:text-primary",
                  selectedParticipantColor === matchup.id2 && selectedUnit === unit.code ? "bg-primary text-white" : "",
                  isParticipantFlashing && selectedUnit === unit.code && "animate-pulse",
                )}
                onClick={(e) => { void handleParticipantClick(e, matchup.id2) }}
                style={isParticipantFlashing && selectedUnit === unit.code && selectedParticipantColor !== matchup.id2
                  ? {
                      backgroundColor: getParticipantFlashColor(),
                      borderRadius: "4px",
                      color: "white",
                    }
                  : undefined}
              >
                <div className="flex items-center gap-0.5">
                  <span className="max-w-[100px] truncate">
                    {matchup.teamInfo2?.isMixed ? matchup.teamInfo2.name : matchup.participant2}
                  </span>
                </div>
                <div className="flex items-center gap-0.5">
                  {(matchup.flag2 || matchup.teamInfo2) && (
                    <Image
                      alt={useOnlyFlag
                        ? matchup.flag2
                        : (matchup.teamInfo2?.useNormalFlag ? matchup.flag2 : (matchup.teamInfo2?.delegation || matchup.flag2))}
                      className={cn("rounded-sm", matchup.teamInfo2?.isSportPicto
                        ? (matchup.teamInfo2?.isDuo ? "h-5 w-8 object-contain" : "h-4 w-6 object-cover")
                        : "h-3.5 w-4.5 object-cover")}
                      height={matchup.teamInfo2?.isSportPicto ? (matchup.teamInfo2?.isDuo ? 20 : 16) : 10}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.src = "/flags/small/un.webp"
                      }}
                      src={useOnlyFlag
                        ? (getCountryFlagIcon(matchup.flag2 as AllISO3Codes) || "/placeholder.svg")
                        : (matchup.teamInfo2?.useNormalFlag
                            ? (getCountryFlagIcon(matchup.flag2 as AllISO3Codes) || "/placeholder.svg")
                            : (matchup.teamInfo2?.flagSrc || "/placeholder.svg"))}
                      width={matchup.teamInfo2?.isSportPicto ? (matchup.teamInfo2?.isDuo ? 32 : 24) : 14}
                    />
                  )}
                  <span className="max-w-[40px] truncate">
                    {useOnlyFlag
                      ? matchup.flag2
                      : (matchup.teamInfo2?.useNormalFlag ? matchup.flag2 : (matchup.teamInfo2?.delegation || matchup.flag2))}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Fecha y hora - single line */}
          {(dateStr || timeStr) && (
            <div className="mb-0.5 flex items-center justify-center gap-1 text-xs text-foreground">
              {dateStr && <span>{dateStr}</span>}
              {dateStr && timeStr && <span>{"\u00B7"}</span>}
              {timeStr && (
                <span className="flex items-center gap-0.5">
                  <Clock className="h-2.5 w-2.5" />
                  <span>{timeStr}</span>
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de configuración de la unidad */}
      <ConfigurableUnitDialog
        disciplineId={unit.metadata?.discipline}
        fieldVisibility={{
          unitDate: true,
          timeStart: true,
          timeEnd: true,
          location: true,
        }}
        isOpen={isConfigModalOpen}
        isSaving={isSavingConfig}
        language={language}
        onClose={handleCloseConfig}
        onSave={handleSaveConfig}
        order={unit.order}
        phaseId={unit.metadata?.phaseCode || ""}
        phaseSettings={phaseSettings}
        title={`Configurar: ${unit.name}`}
        unitData={unit}
      />
    </Link>
  )
}

export default function SidebarUnits({ sidebarType }: { sidebarType: "start-list" | "data-entry" | "integrations" | "main" | "simple-data-entry" }): React.JSX.Element {
  const { disciplineCode, unitCode } = useParams()
  const { data: events } = useQuery(EventQueryOptions.byCode(disciplineCode?.toString().toUpperCase() ?? ""))
  const { state } = useSidebar()
  const t = useTranslations("disciplines.data-entry")
  const tGeneric = useTranslations("generic")
  const tFloating = useTranslations("floating_units")
  const { flashingElements, activeParams } = useParamHintsStore()
  const [search, setSearch] = useQueryState("unit-search")
  const [selectedEvent, setSelectedEvent] = useQueryState("event", {
    clearOnDefault: true,
  })
  const [date, setDate] = useQueryState("unit-date", {
    clearOnDefault: true,
    defaultValue: "",
  })

  // Hook para sincronizar parámetros con channels del scoreboard
  const { syncDateParams, syncParamsWithUserChannels } = useChannelParamsSync({
    onSuccess: (updatedChannels) => {
      console.log(`Parámetros sincronizados con ${updatedChannels.length} channels:`, updatedChannels)
    },
    onError: (error) => {
      console.error("Error sincronizando parámetros con channels:", error)
    },
  })

  const handleParticipantSelect = useCallback((participantId: string) => {
    void syncParamsWithUserChannels({ participant: participantId })
  }, [syncParamsWithUserChannels])

  // Debug optimizado - solo cuando hay un evento seleccionado
  useEffect(() => {
    if (selectedEvent) {
      console.log("🔄 Evento seleccionado:", selectedEvent)
    }
  }, [selectedEvent])

  // Estados para filtros avanzados
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const [filters, setFilters] = useState<FilterValues>({
    status: undefined,
    gender: undefined,
    phase: undefined,
    event: undefined,
    unit: undefined,
    organisation: undefined,
  })

  // Estados para filtros adicionales
  // const [selectedSession, setSelectedSession] = useQueryState("session", {
  //   clearOnDefault: true,
  // })
  const [selectedTatami, setSelectedTatami] = useQueryState("tatami", {
    clearOnDefault: true,
  })
  const [selectedStatus, setSelectedStatus] = useQueryState("status", {
    clearOnDefault: true,
  })

  // Estados para controlar secciones colapsibles
  const [isDatesExpanded, setIsDatesExpanded] = useState(false)
  // const [isSessionsExpanded, setIsSessionsExpanded] = useState(false)
  const [isTatamisExpanded, setIsTatamisExpanded] = useState(false)
  const [isStatusExpanded, setIsStatusExpanded] = useState(false)

  // Configuración para mostrar/ocultar secciones colapsibles
  const DATES_TO_SHOW = 3
  // const SESSIONS_TO_SHOW = 3
  const TATAMIS_TO_SHOW = 3
  const STATUS_TO_SHOW = 3

  const shouldShowFilters = useMemo(() => {
    return Object.values(filters).some(f => f !== undefined)
  }, [filters])

  const parsedEvents = useMemo(() => {
    const result = events?.filter(event => event.code !== undefined).map(event => ({
      label: event.name || languageParser(event.description).long,
      // Usar el mismo formato que el filtro avanzado: "sportEvent-gender-category"
      value: `${event.metadata?.sportEvent}-${event.metadata?.gender}-${event.metadata?.category || "default"}`,
    }))

    // Debug log para ver qué eventos estamos obteniendo
    console.log("🔍 parsedEvents:", result)
    console.log("🔍 events raw data:", events)

    return result
  }, [events])

  const { data: units } = useQuery(StartListQueryOptions.byEventCode(selectedEvent as string))
  const { data: allUnits, isLoading: isLoadingAllUnits, error: allUnitsError } = useQuery({
    ...UnitQueryOptions.getByDisciplineCode((disciplineCode ? (disciplineCode as string).toUpperCase() : "")),
  })

  const { data: allStartLists } = useQuery(StartListQueryOptions.byDisciplineCode(disciplineCode as string))

  // Obtener competiciones por disciplina
  const { data: competitions } = useQuery({
    queryKey: ["competitions", disciplineCode],
    queryFn: async () => competitionService.getAllCompetitionsByDiscipline((disciplineCode as string).toUpperCase()),
    enabled: !!disciplineCode,
    staleTime: 10 * 60 * 1000, // 10 minutos
  })

  // Debug log para ver las competencias
  console.log("🏆 competitions:", competitions)

  // Log del error si existe
  if (allUnitsError) {
    console.warn("Error loading all units, using empty array:", allUnitsError)
  }

  // Establecer el rango inicial de fechas (primera fecha disponible + 1 mes)
  const uniqueDates: string[] = Array.from(new Set(
    Array.isArray(allUnits)
      ? allUnits
          .filter(unit => unit.metadata.discipline.toLowerCase() === (disciplineCode as string)?.toLowerCase())
          .filter(unit => unit.dateInfo?.startDate)
          .map(unit => unit.dateInfo.startDate.split("T")[0])
      : [],
  )).sort() as string[]

  const startListInfo: Record<string, TStartList> = useMemo(() => {
    if (allStartLists) {
      return allStartLists?.reduce((acc, startList) => {
        acc[startList.metadata.unitCode] = startList
        return acc
      }, {} as Record<string, TStartList>)
    }
    return {}
  }, [allStartLists])

  // Extraer sesiones únicas de allUnits basándose en la hora de inicio
  // const uniqueSessions: string[] = Array.from(new Set(
  //   allUnits?.filter(unit => unit.metadata.discipline.toLowerCase() === (disciplineCode as string)?.toLowerCase())
  //     .filter(unit => unit.dateInfo?.startDate)
  //     .map((unit) => {
  //       const startHour = new Date(unit.dateInfo.startDate).getHours()
  //       // Considerar mañana hasta las 12:00 y tarde desde las 12:00
  //       return startHour < 12 ? tFloating("morning") : tFloating("afternoon")
  //     }) || [],
  // )).sort()

  // Extraer tatamis únicos de allUnits
  const uniqueTatamis: string[] = Array.from(new Set(
    Array.isArray(allUnits)
      ? allUnits
          .filter(unit => unit.metadata.discipline.toLowerCase() === (disciplineCode as string)?.toLowerCase())
          .filter(unit => typeof unit.location === "object" && unit.location?.description?.long)
          .map(unit => typeof unit.location === "object" ? unit.location?.description?.long : "")
      : [],
  )).filter(Boolean).sort()

  // Extraer status únicos de allUnits
  const uniqueStatuses: string[] = Array.from(new Set(
    Array.isArray(allUnits)
      ? allUnits
          .filter(unit => unit.metadata.discipline.toLowerCase() === (disciplineCode as string)?.toLowerCase())
          .filter(unit => unit.status)
          .map(unit => unit.status)
      : [],
  )).sort()

  const modalFiltersValues = useMemo(() => {
    return {
      ...filters,
    }
  }, [filters])

  const handleRemoveFilter = useCallback((key: keyof FilterValues) => {
    setFilters(prev => ({
      ...prev,
      [key]: undefined,
    }))
  }, [setFilters])

  // Generar opciones de filtros basadas en las unidades
  const filterOptions: FilterOptions = useMemo(() => {
    const _allUnits = Array.isArray(allUnits)
      ? allUnits.filter(unit => unit.metadata.discipline.toLowerCase() === (disciplineCode as string)?.toLowerCase())
      : []

    // Solo usar los status que se usan en DataEntryStatus + los de programación
    const dataEntryStatuses = ["START_LIST", "GETTING_READY", "RUNNING", "UNOFFICIAL", "OFFICIAL", "PROTESTED", "SCHEDULED", "UNSCHEDULED"]

    return {
      status: dataEntryStatuses
        .map(status => ({ value: status, label: status.replace(/_/g, " ") })),

      gender: [...new Set(_allUnits.map(u => u.metadata?.gender).filter(Boolean))]
        .map(gender => ({ value: gender, label: gender })),

      phase: [...new Set(_allUnits.map(u => u.metadata?.phase).filter(Boolean))]
        .map(phase => ({ value: phase, label: phase })),

      event: [...new Set(_allUnits.map((u) => {
        // Crear un identificador único basado en sportEvent + gender + category
        // Manejar casos donde category podría estar vacía o undefined
        const category = u.metadata.category || "default"
        return `${u.metadata.sportEvent}-${u.metadata.gender}-${category}`
      }).filter(Boolean))]
        .map((eventId) => {
          // Buscar la competencia correspondiente para obtener el nombre completo
          const parts = eventId.split("-")
          const sportEvent = parts[0]
          const gender = parts[1]
          const category = parts.slice(2).join("-") // En caso de que category tenga guiones

          const matchingCompetition = competitions?.find(comp =>
            comp.metadata?.sportEvent === sportEvent
            && comp.metadata?.gender === gender
            && (comp.metadata?.category === category || (!comp.metadata?.category && category === "default")),
          )

          return {
            value: eventId,
            label: matchingCompetition?.name || eventId,
          }
        }),

      unit: [...new Set(_allUnits.map(u => u.metadata?.unitCode).filter(Boolean))]
        .map(unit => ({ value: unit, label: _allUnits.find(u => u.metadata?.unitCode === unit)?.description?.long || unit })),
    }

    // Debug log para ver los eventos disponibles en el filtro
    console.log("🎯 Eventos disponibles en filtro:", filterOptions.event)

    return filterOptions
  }, [allUnits, disciplineCode, competitions])

  const findEventDescription = useCallback((unitCode: string) => {
    // Buscar la unidad correspondiente para obtener su metadata
    const unit = allUnits?.find(u => u.code === unitCode)
    if (!unit)
      return ""

    // Buscar la competición que coincida con el sportEvent, género y categoría de la unit
    const matchingCompetition = competitions?.find(comp =>
      comp.metadata?.sportEvent === unit.metadata?.sportEvent
      && comp.metadata?.gender === unit.metadata?.gender
      && comp.metadata?.category === unit.metadata?.category,
    )

    if (matchingCompetition) {
      // Usar directamente el nombre completo que ya viene en el campo name
      return matchingCompetition.name || ""
    }

    // Fallback: usar description.long + description.short de la unit
    if (unit.description?.long || unit.description?.short) {
      const long = unit.description.long || ""
      const short = unit.description.short || ""
      return `${long} ${short}`.trim()
    }

    // Fallback final: construir el título manualmente
    const { gender, sportEvent, phase } = unit.metadata || {}
    let matchTitle = ""

    if (gender === "M")
      matchTitle += "Men's "
    else if (gender === "W")
      matchTitle += "Women's "
    else if (gender === "X")
      matchTitle += "Mixed "

    if (sportEvent)
      matchTitle += `${sportEvent} `
    if (phase)
      matchTitle += phase.replace(/-/g, " ").trim()

    return matchTitle.trim()
  }, [allUnits, competitions])

  const filteredUnits = useMemo(() => {
    const _allUnits = Array.isArray(allUnits)
      ? allUnits.filter(unit => unit.metadata.discipline.toLowerCase() === (disciplineCode as string)?.toLowerCase())
      : []

    // Si hay un evento seleccionado, filtrar por ese evento específico (incluyendo categoría)
    let unitsToUse = _allUnits
    if (selectedEvent) {
      // selectedEvent viene en formato "sportEvent-gender-category"
      const parts = selectedEvent.split("-")
      const sportEvent = parts[0]
      const gender = parts[1]
      const categoryValue = parts.slice(2).join("-") || "default" // Manejar categorías con guiones

      unitsToUse = _allUnits.filter(unit => {
        // Filtro estricto: debe coincidir exactamente con sportEvent, gender y category
        const unitSportEvent = unit.metadata.sportEvent
        const unitGender = unit.metadata.gender
        const unitCategory = unit.metadata.category || "default"
        
        // Si la categoría seleccionada es "default", solo mostrar unidades sin categoría o con "GENERAL"
        if (categoryValue === "default") {
          return unitSportEvent === sportEvent 
            && unitGender === gender 
            && (unitCategory === "default" || unitCategory === "GENERAL" || !unit.metadata.category)
        }
        
        // Para categorías específicas, debe coincidir exactamente
        return unitSportEvent === sportEvent 
          && unitGender === gender 
          && unitCategory === categoryValue
      })

    }

    const parsedUnitToUse = unitsToUse?.map(unit => ({
      ...unit,
      eventDescription: findEventDescription(unit.code),
    }))

    const filtered = parsedUnitToUse?.filter((unit) => {
      const unitDate = new UTCDate(unit.dateInfo.startDate)
      const searchMatch = unit.metadata.unit.toLowerCase().includes(search?.toLowerCase() ?? "") || languageParser(unit.description).long.toLowerCase().includes(search?.toLowerCase() ?? "") || unit.eventDescription.toLowerCase().includes(search?.toLowerCase() ?? "")

      const dateMatch = date
        ? (date === format(unitDate, "yyyy-MM-dd"))
        : true

      // Filtros avanzados
      const matchesStatus = filters.status === undefined || unit.status === filters.status
      const matchesGender = filters.gender === undefined || unit.metadata?.gender === filters.gender
      const matchesPhase = filters.phase === undefined || unit.metadata?.phase === filters.phase
      const matchesEvent = filters.event === undefined || `${unit.metadata.sportEvent}-${unit.metadata.gender}-${unit.metadata.category || "default"}` === filters.event
      const matchesUnit = filters.unit === undefined || unit.metadata?.unit === filters.unit

      // Filtros adicionales para sesiones y tatamis
      /* const matchesSession = selectedSession === undefined || (() => {
        const startHour = new Date(unit.dateInfo.startDate).getHours()
        const unitSession = startHour < 12 ? tFloating("morning") : tFloating("afternoon")
        return unitSession === selectedSession
      })() */
      const matchesTatami = selectedTatami === null || selectedTatami === undefined || (
        typeof unit.location === "object" && unit.location?.description?.long === selectedTatami
      )
      const matchesSelectedStatus = selectedStatus === null || selectedStatus === undefined || unit.status === selectedStatus

      return searchMatch && dateMatch && matchesStatus && matchesGender && matchesPhase && matchesEvent && matchesUnit && matchesTatami && matchesSelectedStatus
    })

    // Ordenar las units con lógica especial para poules
    if (filtered && filtered.length > 0) {
      const isJJBEnv = env.NEXT_PUBLIC_CHAMPIONSHIP === "JJB"
      if (isJJBEnv) {
        return filtered.sort((a, b) => {
        // Función helper para verificar si una unit necesita ordenamiento especial
          const needsSpecialOrdering = (unit: Unit) => {
            // Verificar si el nombre contiene "poules" (case insensitive)
            const nameContainsPoules = unit.name?.toLowerCase().includes("poules")

            // Verificar si el phase en metadata comienza con "GP"
            const phaseStartsWithGP = unit.metadata?.phase?.startsWith("GP")

            const isSpecial = nameContainsPoules || phaseStartsWithGP

            // Debug log para verificar el funcionamiento
            if (isSpecial) {
              console.log(`🔄 Unidad especial detectada: ${unit.name} - Order: ${unit.order}`, {
                nameContainsPoules,
                phaseStartsWithGP,
                phase: unit.metadata?.phase,
                order: unit.order,
              })
            }

            return isSpecial
          }

          const aSpecial = needsSpecialOrdering(a)
          const bSpecial = needsSpecialOrdering(b)

          // Si ambas unidades necesitan ordenamiento especial, ordenar por order descendente
          if (aSpecial && bSpecial) {
            const orderA = a.order || 0
            const orderB = b.order || 0
            return orderB - orderA // Descendente: mayor order primero
          }

          // Si solo una necesita ordenamiento especial, mantener orden cronológico pero agrupar
          if (aSpecial && !bSpecial) {
            return -1 // Las unidades especiales van primero
          }

          if (!aSpecial && bSpecial) {
            return 1 // Las unidades especiales van primero
          }

          // Para unidades normales, ordenar cronológicamente
          if (!a.dateInfo?.startDate || !b.dateInfo?.startDate) {
            return 0
          }

          // Convertir fechas UTC a zona horaria de Brasilia para comparación
          const dateA = new Date(a.dateInfo.startDate)
          const dateB = new Date(b.dateInfo.startDate)

          // Ajustar a zona horaria de Brasilia (UTC-3)
          const brasiliaDateA = new Date(dateA.getTime() + (3 * 60 * 60 * 1000))
          const brasiliaDateB = new Date(dateB.getTime() + (3 * 60 * 60 * 1000))

          // Ordenar por fecha y hora (cronológicamente)
          return brasiliaDateA.getTime() - brasiliaDateB.getTime()
        })
      }
      else {
        return filtered
      }
    }

    return filtered
  }, [search, date, allUnits, disciplineCode, selectedEvent, filters, findEventDescription, selectedTatami, selectedStatus])

  function handleDateClick(newDate: string) {
    if (date === newDate) {
      void setDate(null)
      // Sincronizar fecha vacía (mostrar todas las fechas) con channels
      void syncDateParams(null)
    }
    else {
      void setDate(newDate)
      // Sincronizar nueva fecha con channels
      void syncDateParams(newDate)
    }
  }

  const handleClearFilters = () => {
    void setSearch("")
    void setDate(null)
    // void setSelectedSession(null)
    void setSelectedTatami(null)
    void setSelectedStatus(null)
    setFilters({
      status: undefined,
      gender: undefined,
      phase: undefined,
      event: undefined,
      unit: undefined,
      organisation: undefined,
    })

    // Sincronizar limpieza de filtros con channels
    void syncDateParams(null)
  }

  // Función helper para manejar clicks de tatami con sincronización
  const handleTatamiClick = (tatami: string) => {
    const newValue = selectedTatami === tatami ? null : tatami
    void setSelectedTatami(newValue)
  }

  // Función helper para limpiar selección de tatami con sincronización
  const handleClearTatami = () => {
    void setSelectedTatami(null)
  }

  // Función helper para manejar clicks de status
  const handleStatusClick = (status: string) => {
    const newValue = selectedStatus === status ? null : status
    void setSelectedStatus(newValue)
  }

  // Función helper para limpiar selección de status
  const handleClearStatus = () => {
    void setSelectedStatus(null)
  }

  // Formatear fecha para mostrar en zona horaria de Brasilia
  const formatDate = (dateString: string) => {
    // Crear fecha en UTC y convertir a zona horaria de Brasilia
    const utcDate = new Date(`${dateString}T00:00:00.000Z`)
    const brasiliaDate = new Date(utcDate.getTime() + (3 * 60 * 60 * 1000))

    const day = brasiliaDate.getDate().toString().padStart(2, "0")
    const month = (brasiliaDate.getMonth() + 1).toString().padStart(2, "0")
    const shortYear = brasiliaDate.getFullYear().toString().slice(-2)

    return `${day}/${month}/${shortYear}`
  }

  // Get flash color for date parameters
  const getDateFlashColor = () => {
    if (!flashingElements.has("date"))
      return ""
    const dateParam = activeParams.find(p => p.type === "date")
    return dateParam?.color || "#ffb300"
  }

  const isDateFlashing = flashingElements.has("date")

  // Get flash color for participant parameters
  const _getParticipantFlashColor = () => {
    if (!flashingElements.has("participant"))
      return ""
    const participantParam = activeParams.find(p => p.type === "participant")
    return participantParam?.color || "#10b981"
  }

  const _isParticipantFlashing = flashingElements.has("participant")

  return (
    <>
      <Sidebar
        className={cn("hidden w-30 flex-1 md:flex relative", state === "collapsed" && "md:hidden")}
        collapsible="none"
        variant="inset"
      >
        <SidebarHeader className="flex flex-col gap-3.5 border-b p-4">
          <div className="flex w-full items-center justify-between">
            <div className="text-base font-medium">
              {t("selection.event")}
            </div>
            <Button
              className="h-8 w-8 p-0"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              size="sm"
              variant="ghost"
            >
              <Icon
                className="h-4 w-4"
                icon={filtersExpanded ? "lucide:eye-off" : "lucide:eye"}
              />
            </Button>
          </div>
          <ExtendedCombobox
            items={parsedEvents}
            onChange={value => void setSelectedEvent(value as string)}
            placeholder={t("selection.event")}
            value={selectedEvent ?? undefined}
          />

          {/* Barra de búsqueda y filtros */}
          <div className="space-y-1.5 md:space-y-2">
            <div className="flex flex-col gap-1.5">
              <Input
                className="h-6 w-full bg-background text-xs text-foreground shadow-none md:h-8 md:text-sm"
                onChange={e => void setSearch(e.target.value)}
                placeholder={tFloating("search_placeholder")}
                value={search ?? ""}
              />
              <Button
                className="h-6 px-2 text-xs md:h-8 md:text-sm"
                onClick={() => setShowFiltersModal(true)}
                variant="outline"
              >
                <Filter className="mr-1 h-3 w-3" />
                {tFloating("filters")}
              </Button>
              {
                shouldShowFilters && (
                  <>
                    <div className="flex flex-wrap gap-1">
                      {
                        Object.entries(filters).filter(([_key, f]) => f !== undefined).map(([key, f]) => (
                          f
                            ? (
                                <Button
                                  key={f.toString()}
                                  className="h-5 bg-primary px-1.5 py-0 text-[10px] md:h-6 md:px-2 md:text-xs hover:bg-primary/90"
                                  onClick={() => handleRemoveFilter(key as keyof FilterValues)}
                                  variant="default"
                                >
                                  <span className="text-primary-foreground mr-1">
                                    <Icon className="size-3" icon="lucide:x" />
                                  </span>
                                  {f.toString()}
                                </Button>
                              )
                            : null
                        ))
                      }
                    </div>
                    <div className="h-px w-full bg-border" />
                  </>

                )
              }
            </div>

            {/* Contenedor de filtros que se puede mostrar/ocultar */}
            {filtersExpanded && (
              <div className="space-y-3 border-t pt-3">
                {/* Fechas con colores del FloatingUnits */}
                <div className="space-y-1">
                  <div className="text-xs font-medium text-muted-foreground">{tFloating("dates")}</div>
                  <div className="flex flex-wrap gap-1">
                    {uniqueDates.slice(0, isDatesExpanded ? undefined : DATES_TO_SHOW).map(dateStr => (
                      <Button
                        key={dateStr}
                        className={cn("text-[10px] md:text-xs py-0 px-1.5 md:px-2 h-5 md:h-6 transition-all", {
                          "bg-primary text-primary-foreground": date === dateStr,
                          "animate-pulse": isDateFlashing && date !== dateStr,
                        })}
                        onClick={() => handleDateClick(dateStr)}
                        style={isDateFlashing && date !== dateStr
                          ? {
                              backgroundColor: getDateFlashColor(),
                              borderColor: getDateFlashColor(),
                              color: "white",
                            }
                          : undefined}
                        variant={date === dateStr ? "default" : "outline"}
                      >
                        {formatDate(dateStr)}
                      </Button>
                    ))}
                    <Button
                      className={cn("text-[10px] md:text-xs py-0 px-1.5 md:px-2 h-5 md:h-6 transition-all", {
                        "bg-chart-2 hover:bg-chart-2/90": date === null,
                        "animate-pulse": isDateFlashing && date !== null,
                      })}
                      onClick={() => {
                        void setDate(null)
                        void syncDateParams(null)
                      }}
                      style={isDateFlashing && date !== null
                        ? {
                            backgroundColor: getDateFlashColor(),
                            borderColor: getDateFlashColor(),
                            color: "white",
                          }
                        : undefined}
                      variant={date === null ? "default" : "outline"}
                    >
                      {tFloating("all_dates")}
                    </Button>
                    <Button
                      className="h-5 px-1.5 py-0 text-[10px] md:h-6 md:px-2 md:text-xs"
                      onClick={handleClearFilters}
                      variant="outline"
                    >
                      {tFloating("clear")}
                    </Button>
                  </div>

                  {/* Botón para expandir/contraer fechas */}
                  {uniqueDates.length > DATES_TO_SHOW && (
                    <Button
                      className="h-5 w-full px-1.5 py-0 text-[10px] md:h-6 md:px-2 md:text-xs"
                      onClick={() => setIsDatesExpanded(!isDatesExpanded)}
                      variant="ghost"
                    >
                      {isDatesExpanded
                        ? (
                            <>
                              {tFloating("show_less")}
                              <ChevronUp className="ml-1 h-3 w-3" />
                            </>
                          )
                        : (
                            <>
                              {tFloating("show_more")}
                              {" "}
                              (
                              {uniqueDates.length - DATES_TO_SHOW}
                              {" "}
                              more)
                              <ChevronDown className="ml-1 h-3 w-3" />
                            </>
                          )}
                    </Button>
                  )}
                </div>

                {/* Filtro de Sesiones - OCULTO */}
                {/* {uniqueSessions.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">{tFloating("sessions")}</div>
                <div className="flex flex-wrap gap-1">
                  {uniqueSessions.slice(0, isSessionsExpanded ? undefined : SESSIONS_TO_SHOW).map(session => (
                    <Button
                      key={session}
                      className={cn("text-[10px] md:text-xs py-0 px-1.5 md:px-2 h-5 md:h-6", {
                        "bg-primary text-primary-foreground": selectedSession === session,
                      })}
                      onClick={() => void setSelectedSession(selectedSession === session ? null : session)}
                      variant={selectedSession === session ? "default" : "outline"}
                    >
                      {session}
                    </Button>
                  ))}
                  <Button
                    className={cn("text-[10px] md:text-xs py-0 px-1.5 md:px-2 h-5 md:h-6", {
                      "bg-chart-2 hover:bg-chart-2/90": selectedSession === null,
                    })}
                    onClick={() => void setSelectedSession(null)}
                    variant={selectedSession === null ? "default" : "outline"}
                  >
                    {tFloating("all_sessions")}
                  </Button>
                </div>

                {/* Botón para expandir/contraer sesiones */}
                {/* {uniqueSessions.length > SESSIONS_TO_SHOW && (
                  <Button
                    className="h-5 w-full px-1.5 py-0 text-[10px] md:h-6 md:px-2 md:text-xs"
                    onClick={() => setIsSessionsExpanded(!isSessionsExpanded)}
                    variant="ghost"
                  >
                    {isSessionsExpanded
                      ? (
                          <>
                            {tFloating("show_less")}
                            <ChevronUp className="ml-1 h-3 w-3" />
                          </>
                        )
                      : (
                          <>
                            {tFloating("show_more")}
                            {" "}
                            (
                            {uniqueSessions.length - SESSIONS_TO_SHOW}
                            {" "}
                            more)
                            <ChevronDown className="ml-1 h-3 w-3" />
                          </>
                        )}
                  </Button>
                )} */}
                {/* </div>
            )} */}

                {/* Filtro de Tatamis */}
                {uniqueTatamis.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-muted-foreground">{tFloating("locations")}</div>
                    <div className="flex flex-wrap gap-1">
                      {uniqueTatamis.slice(0, isTatamisExpanded ? undefined : TATAMIS_TO_SHOW).map(tatami => (
                        <Button
                          key={tatami}
                          className={cn("text-[10px] md:text-xs py-0 px-1.5 md:px-2 h-5 md:h-6", {
                            "bg-primary text-primary-foreground": selectedTatami === tatami,
                          })}
                          onClick={() => handleTatamiClick(tatami)}
                          variant={selectedTatami === tatami ? "default" : "outline"}
                        >
                          {tatami}
                        </Button>
                      ))}
                      <Button
                        className={cn("text-[10px] md:text-xs py-0 px-1.5 md:px-2 h-5 md:h-6", {
                          "bg-chart-2 hover:bg-chart-2/90": selectedTatami === null || selectedTatami === undefined,
                        })}
                        onClick={handleClearTatami}
                        variant={selectedTatami === null || selectedTatami === undefined ? "default" : "outline"}
                      >
                        {tFloating("all_locations")}
                      </Button>
                    </div>

                    {/* Botón para expandir/contraer tatamis */}
                    {uniqueTatamis.length > TATAMIS_TO_SHOW && (
                      <Button
                        className="h-5 w-full px-1.5 py-0 text-[10px] md:h-6 md:px-2 md:text-xs"
                        onClick={() => setIsTatamisExpanded(!isTatamisExpanded)}
                        variant="ghost"
                      >
                        {isTatamisExpanded
                          ? (
                              <>
                                {tFloating("show_less")}
                                <ChevronUp className="ml-1 h-3 w-3" />
                              </>
                            )
                          : (
                              <>
                                {tFloating("show_more")}
                                {" "}
                                (
                                {uniqueTatamis.length - TATAMIS_TO_SHOW}
                                {" "}
                                more)
                                <ChevronDown className="ml-1 h-3 w-3" />
                              </>
                            )}
                      </Button>
                    )}
                  </div>
                )}

                {/* Filtro de Status */}
                {uniqueStatuses.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-muted-foreground">{tGeneric("status")}</div>
                    <div className="flex flex-wrap gap-1">
                      {uniqueStatuses.slice(0, isStatusExpanded ? undefined : STATUS_TO_SHOW).map(status => (
                        <Button
                          key={status}
                          className={cn("text-[10px] md:text-xs py-0 px-1.5 md:px-2 h-5 md:h-6", {
                            "bg-primary text-primary-foreground": selectedStatus === status,
                          })}
                          onClick={() => handleStatusClick(status)}
                          variant={selectedStatus === status ? "default" : "outline"}
                        >
                          {status}
                        </Button>
                      ))}
                      <Button
                        className={cn("text-[10px] md:text-xs py-0 px-1.5 md:px-2 h-5 md:h-6", {
                          "bg-chart-2 hover:bg-chart-2/90": selectedStatus === null || selectedStatus === undefined,
                        })}
                        onClick={handleClearStatus}
                        variant={selectedStatus === null || selectedStatus === undefined ? "default" : "outline"}
                      >
                        {tGeneric("all_status")}
                      </Button>
                    </div>

                    {/* Botón para expandir/contraer status */}
                    {uniqueStatuses.length > STATUS_TO_SHOW && (
                      <Button
                        className="h-5 w-full px-1.5 py-0 text-[10px] md:h-6 md:px-2 md:text-xs"
                        onClick={() => setIsStatusExpanded(!isStatusExpanded)}
                        variant="ghost"
                      >
                        {isStatusExpanded
                          ? (
                              <>
                                {tFloating("show_less")}
                                <ChevronUp className="ml-1 h-3 w-3" />
                              </>
                            )
                          : (
                              <>
                                {tFloating("show_more")}
                                {" "}
                                (
                                {uniqueStatuses.length - STATUS_TO_SHOW}
                                {" "}
                                more)
                                <ChevronDown className="ml-1 h-3 w-3" />
                              </>
                            )}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup className="px-0">
            <SidebarGroupContent>
              {isLoadingAllUnits
                ? (
                    <div className="flex h-full items-center justify-center">
                      <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-primary md:h-4 md:w-4"></div>
                    </div>
                  )
                : allUnitsError
                  ? (
                      <div className="py-1 text-center text-[10px] text-muted-foreground md:py-2 md:text-xs">
                        {tFloating("no_units_available")}
                      </div>
                    )
                  : allUnits
                    ? allUnits?.length === 0
                      ? (
                          <div className="p-2">
                            <AlertWrapper
                              description={t("selection.no_units_description")}
                              icon={<InfoIcon className="size-4" />}
                              title={t("selection.no_units")}
                              variant="info"
                            >
                            </AlertWrapper>
                          </div>
                        )
                      : filteredUnits?.length === 0
                        ? (
                            <div className="p-2">
                              <AlertWrapper
                                description={t("selection.no_units_match_description")}
                                icon={<InfoIcon className="size-4" />}
                                title={t("selection.no_units_match")}
                                variant="info"
                              >
                              </AlertWrapper>
                            </div>
                          )
                        : (
                            <div key={`units-${selectedEvent || 'all'}-${date || 'all'}-${search || 'all'}`} className="flex flex-col gap-3 p-2">
                              {filteredUnits?.map((unit, index) => (
                                <UnitItem
                                  key={`${unit.code}-${index}-${selectedEvent || 'all'}`}
                                  competitions={competitions}
                                  eventCode={selectedEvent || undefined}
                                  eventDescription={unit.eventDescription}
                                  onParticipantSelect={handleParticipantSelect}
                                  search={search || undefined}
                                  selectedDate={date}
                                  selectedStatus={selectedStatus || undefined}
                                  selectedTatami={selectedTatami || undefined}
                                  selectedUnit={unitCode?.toString()}
                                  startListParentData={startListInfo?.[unit.code] || undefined}
                                  type={sidebarType}
                                  unit={unit}
                                />
                              ))}
                            </div>
                          )
                    : (
                        <div className="p-2">
                          <AlertWrapper
                            description={t("selection.no_event_description")}
                            icon={<InfoIcon className="size-4" />}
                            title={t("selection.no_event")}
                            variant="info"
                          >
                          </AlertWrapper>
                        </div>
                      )}

            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>

      {/* Modal de filtros avanzados optimizado */}
      {showFiltersModal && (
        <OptimizedFilters
          config={{
            status: true,
            gender: true,
            phase: true,
            event: true,
            unit: true,
          }}
          onApplyFilters={() => setShowFiltersModal(false)}
          onClearFilters={handleClearFilters}
          onOpenChange={setShowFiltersModal}
          onValuesChange={(newValues: FilterValues) => {
            setFilters(newValues)
          }}
          open={showFiltersModal}
          options={filterOptions}
          values={modalFiltersValues as FilterValues}
        />
      )}

    </>
  )
}
